import { useCallback } from "react"

import type { PlotReqConfig } from "types/dashboard.types"
import type { DateRange } from "types/date.types"
import type { TelemetryFetcher, QualityFetcher } from "types/telemetries.types"

import {
  useLazyDeviceTelemetryQuery,
  useLazyDeviceTelemetryQualityQuery,
} from "features/api"
import {
  useMultiEntityTelemetry,
  type UseMultiEntityTelemetryReturn,
} from "./useMultiEntityTelemetry"

interface UseMultiDeviceTelemetryProps {
  deviceIds: number[]
  reqConfig: PlotReqConfig
  dateRange: DateRange | null
  isDateRangeValid: boolean
}

export const useMultiDeviceTelemetry = ({
  deviceIds,
  reqConfig,
  dateRange,
  isDateRangeValid,
}: UseMultiDeviceTelemetryProps): UseMultiEntityTelemetryReturn => {
  const [fetchDeviceTelemetry] = useLazyDeviceTelemetryQuery()
  const [fetchDeviceTelemetryQuality] = useLazyDeviceTelemetryQualityQuery()
  const { measurement } = reqConfig

  const fetchTelemetry = useCallback<TelemetryFetcher>(
    (id, params, preferCacheValue) =>
      fetchDeviceTelemetry(
        { id, params: { ...params, ...(measurement && { measurement }) } },
        preferCacheValue,
      ).unwrap(),
    [fetchDeviceTelemetry, measurement],
  )

  const fetchTelemetryQuality = useCallback<QualityFetcher>(
    (id, params) =>
      fetchDeviceTelemetryQuality(
        { id, params: { ...params, ...(measurement && { measurement }) } },
        true,
      ).unwrap(),
    [fetchDeviceTelemetryQuality, measurement],
  )

  return useMultiEntityTelemetry({
    ids: deviceIds,
    reqConfig,
    dateRange,
    isDateRangeValid,
    fetchTelemetry,
    fetchTelemetryQuality,
  })
}
